import React, { ChangeEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import MultilineInputComponent from './MultilineInputComponent';

interface AutoResizeTextboxProps {
    value: string;
    placeHolder?: string;
    onKeyPress?: (e: KeyboardEvent) => void;
    changeAction?: (e: ChangeEvent<HTMLInputElement>) => void;
    boldBorder?: boolean;
}

const AutoResizeTextbox = ({ value, placeHolder, onKeyPress, changeAction, boldBorder }: AutoResizeTextboxProps) => {
    const REPLY_MIN_HEIGHT = 44;
    const REPLY_BORDER = 4;
    const [textAreaHeight, setTextAreaHeight] = useState(REPLY_MIN_HEIGHT);
    const [textAreaWidth, setTextAreaWidth] = useState(0);
    const textAreaRef = useRef<any>();

    const updateReplyHeight = (height: number) => {
        if (height > REPLY_MIN_HEIGHT) {
            setTextAreaHeight(height + REPLY_BORDER);
        } else {
            setTextAreaHeight(REPLY_MIN_HEIGHT);
        }
    }

    useLayoutEffect(() => {
        if (textAreaRef.current) {
            setTextAreaWidth(textAreaRef.current.offsetWidth);
        }
    }, [textAreaRef]);

    useEffect(() => {
        function handleWindowResize() {
            if (textAreaRef.current) {
                setTextAreaWidth(textAreaRef.current.offsetWidth);
            }
        }

        window.addEventListener('resize', handleWindowResize);

        return () => { window.removeEventListener('resize', handleWindowResize); }
    }, []);

    return (<div style={{width: '100%'}}>
        <MultilineInputComponent
            placeHolder={placeHolder}
            value={value}
            changeAction={changeAction}
            onKeyPress={onKeyPress}
            noResize
            ref={textAreaRef}
            height={textAreaHeight}
        />
        <ShadowTextComponent value={value} width={textAreaWidth - REPLY_BORDER} heightChanged={updateReplyHeight} />
    </div>);
}

export default AutoResizeTextbox;

interface ShadowTextProps {
    width: number;
    value: string;
    heightChanged: (h: number) => void;
}

const ShadowTextComponent = ({ width, value, heightChanged }: ShadowTextProps) => {
    const [height, setHeight] = useState(0);
    const [displayValue, setDisplayValue] = useState(value);
    const ref = useRef<any>();

    useEffect(() => {
        let newValue = value;
        if (value.length > 0) {
            const finalChar = value[value.length - 1];
            if (finalChar === '\r' || finalChar === '\n') {
                // Force a new blank line using non-breaking space
                newValue = value + '\xa0';
            }
        }
        setDisplayValue(newValue);
    }, [value]);

    useEffect(() => {
        if (ref.current) {
            const elementHeight = ref.current.clientHeight;
            if (elementHeight !== height) {
                setHeight(elementHeight);
                heightChanged(elementHeight);
            }
        }
    }, [displayValue, height, heightChanged]);

    return (<ShadowTextContainer>
        <ShadowTextElement width={width} ref={ref}>{displayValue}</ShadowTextElement>
    </ShadowTextContainer>);
}

const ShadowTextContainer = styled.div`
    position: relative;
    overflow: hidden;
`;

const ShadowTextElement = styled.div`
    position: absolute;
    right: -100px;
    width: ${p => p.width}px;
    white-space: pre-wrap;
    font-size: 14px;
    padding: 8px;
    line-height: 24px;
    max-height: 600px;
    @media (max-width: 480px) {
        // leave room for reply button
        padding-right: 40px;
    }
`;